import { useAuth } from "@/lib/auth";
import api from "@/lib/api";
import { useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { uploadPhoto } from "@/lib/imageUpload";
import { PhotoLightbox } from "@/components/PhotoLightbox";
import { AuthImage } from "@/components/AuthImage";
import { toast } from "sonner";

const MATERIALS = ["GAP40", "GAP65", "GAP20", "Sand", "Topsoil", "Hardfill", "Demo", "Clay", "Metal dust", "Scoria", "Drainage metal"];

export default function RunningSheetLoadDialog({ jobs = [], dispatch, initial, retryStorageKey, onClose, onSave }) {
  const {user} = useAuth();
  const simple = user?.role === "field" && !!dispatch;
  const [recovered] = useState(() => { try { return retryStorageKey ? JSON.parse(sessionStorage.getItem(retryStorageKey)) : null; } catch { return null; } });
  const nowTime = () => new Date().toTimeString().slice(0, 5);
  const [f, setF] = useState(recovered?.form || {
    time: nowTime(), material: dispatch?.material || "", from_location: dispatch?.pickup_location || "", to_location: dispatch?.dropoff_location || "",
    tonnes: "", cubic_m: "", docket_number: "", job_id: dispatch?.linked_job_id || "", notes: "", docket_path: null,
    ...(initial ? Object.fromEntries(["time", "material", "from_location", "to_location", "tonnes", "cubic_m", "docket_number", "job_id", "notes", "docket_path"].map(k => [k, initial[k] ?? (k === "docket_path" ? null : "")])) : {}),
  });
  const [busy, setBusy] = useState(false);
  const saving = useRef(false);
  const [submissionId] = useState(() => recovered?.payload?.submission_id || crypto.randomUUID());
  const [photoName, setPhotoName] = useState(initial?.docket_path ? "Saved docket photo" : "");
  const [viewPhoto, setViewPhoto] = useState(false);
  const [attempt, setAttempt] = useState(recovered?.payload || null);
  const primaryUnit = dispatch?.quantity_progress?.[0]?.unit || dispatch?.master_job?.target_unit;
  const [uploading, setUploading] = useState(false);
  const [scanning, setScanning] = useState(false);
  const [suggestions, setSuggestions] = useState(null);
  const [scanError, setScanError] = useState("");
  const scanInput = useRef(null);
  const scanPhoto = async e => {
    const file = e.target.files?.[0]; e.target.value = "";
    if (!file || scanning || uploading || busy || attempt) return;
    setScanning(true); setSuggestions(null); setScanError("");
    let attached = false;
    try {
      const {path} = await uploadPhoto(file, "trucking_docket", undefined, {onlineOnly:true});
      setF(current => ({...current, docket_path:path})); setPhotoName(file.name); attached = true;
      const body = new FormData(); body.append("file", file);
      const {data} = await api.post("/truck-sheets/scan-docket", body, {__flOnlineOnly:true, timeout:120000, headers:{"Content-Type":"multipart/form-data"}});
      const fields = Object.fromEntries(Object.entries(data.fields || {}).filter(([key,value]) => ["material","from_location","to_location","docket_number","time","tonnes","cubic_m"].includes(key) && value !== null && value !== ""));
      if (Object.keys(fields).length) setSuggestions(fields);
      else setScanError("No readable load details found. The photo is attached; enter the details manually or try a clearer photo.");
    } catch {setScanError(attached ? "The photo is attached, but the AI scan failed. Enter the details manually or try another scan." : "Could not upload the docket. Please try again when you are online.");}
    finally {setScanning(false);}
  };
  const pickPhoto = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setSuggestions(null); setScanError("");
    setUploading(true);
    try {
      const { path } = await uploadPhoto(file, "trucking_docket");
      setF(current => ({ ...current, docket_path: path }));
      setPhotoName(file.name);
    } catch { toast.error("Docket upload failed — try again"); }
    finally { setUploading(false); }
  };
  const set = (k) => (e) => setF({ ...f, [k]: e?.target ? e.target.value : e });

  const save = async () => {
    if (!f.material.trim()) { toast.error("Material is required"); return; }
    if (saving.current || uploading || scanning) return;
    if ([f.tonnes, f.cubic_m].some(v => v !== "" && (!Number.isFinite(Number(v)) || Number(v) < 0))) {
      toast.error("Quantities must be zero or greater"); return;
    }
    saving.current = true; setBusy(true);
    try {
      const payload = attempt || {
        ...f,
        submission_id: submissionId,
        ...(dispatch ? { dispatch_id: dispatch.id } : {}),
        tonnes: f.tonnes === "" ? null : Number(f.tonnes),
        cubic_m: f.cubic_m === "" ? null : Number(f.cubic_m),
        job_id: f.job_id || null,
      };
      if (retryStorageKey) {
        try { sessionStorage.setItem(retryStorageKey, JSON.stringify({ form: f, payload })); }
        catch { throw new Error("Could not retain this load for safe retry. Keep this form open and enable browser session storage before saving."); }
      }
      setAttempt(payload);
      await onSave(payload);
      if (retryStorageKey) sessionStorage.removeItem(retryStorageKey);
    } catch (e) {
      if ([400, 403, 404, 422].includes(e.response?.status) && !attempt) {
        setAttempt(null);
        if (retryStorageKey) sessionStorage.removeItem(retryStorageKey);
      }
      toast.error(typeof e.response?.data?.detail === "string" ? e.response.data.detail : e.message || "Save could not be confirmed. Retry this same load.");
    }
    finally { saving.current = false; setBusy(false); }
  };

  const scanControls = (<div className="rounded-lg border p-3 space-y-2">
          <input ref={scanInput} aria-label="Choose docket to scan" className="hidden" type="file" accept="image/*" capture="environment" onChange={scanPhoto} />
          <Button type="button" variant="outline" disabled={busy || uploading || scanning || !!attempt} onClick={() => scanInput.current?.click()}>{scanning ? "Scanning docket…" : "AI scan docket"}</Button>
          <p className="text-xs text-muted-foreground">Take a photo or choose a docket. Review the suggested details before saving. The photo is attached when you save the load.</p>
          {scanError && <p role="alert" className="text-sm text-amber-700">{scanError}</p>}
          {suggestions && <div className="space-y-2" role="region" aria-label="Docket scan suggestions"><p className="text-sm font-semibold">Review scanned details</p><dl className="text-sm space-y-1">{Object.entries(suggestions).map(([key,value]) => <div key={key} className="flex justify-between gap-3"><dt>{{material:"Material",from_location:"From",to_location:"To",docket_number:"Docket number",time:"Time",tonnes:"Tonnes (net)",cubic_m:"Cubic metres"}[key]}</dt><dd className="text-right break-words">{value}</dd></div>)}</dl><p className="text-xs text-muted-foreground">Check the net weight and units against the docket. Applying these replaces the matching fields below; you can edit them before saving.</p><div className="flex gap-2"><Button type="button" disabled={busy || !!attempt} onClick={() => {setF(current => ({...current,...suggestions}));setSuggestions(null);}}>Use scanned details</Button><Button type="button" variant="ghost" onClick={() => setSuggestions(null)}>Keep my entries</Button></div></div>}
        </div>);
  const basicFields = <>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="label-tag">Load time</Label>
              <Input type="time" value={f.time} onChange={set("time")} className="mt-1.5 rounded-sm border-zinc-400" data-testid="load-time-input" />
            </div>
            <div>
              <Label className="label-tag">Material *</Label>
              <Input list="material-options" value={f.material} onChange={set("material")} placeholder="e.g. GAP40"
                     className="mt-1.5 rounded-sm border-zinc-400" data-testid="load-material-input" />
              <datalist id="material-options">
                {MATERIALS.map((m) => <option key={m} value={m} />)}
              </datalist>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="label-tag">From</Label>
              <Input value={f.from_location} onChange={set("from_location")} placeholder="Quarry / site"
                     className="mt-1.5 rounded-sm border-zinc-400" data-testid="load-from-input" />
            </div>
            <div>
              <Label className="label-tag">To</Label>
              <Input value={f.to_location} onChange={set("to_location")} placeholder="Tip site / job"
                     className="mt-1.5 rounded-sm border-zinc-400" data-testid="load-to-input" />
            </div>
          </div>
</>;
  const jobField = <>          <div>
            <Label className="label-tag">Linked job (optional)</Label>
            <select value={f.job_id} onChange={set("job_id")}
                    className="mt-1.5 w-full h-10 px-3 text-sm bg-background border border-border rounded-sm focus:outline-none focus:border-[#FF5F15]"
                    data-testid="load-job-select">
              <option value="">No job</option>
              {dispatch?.linked_job_id && !jobs.some(j => j.id === dispatch.linked_job_id) && <option value={dispatch.linked_job_id}>{dispatch.linked_job?.title || "Assigned job"}</option>}
              {jobs.map((j) => <option key={j.id} value={j.id}>{(j.job_number ? j.job_number + " — " : "") + j.title}</option>)}
            </select>
          </div>
</>;
  return (
    <Dialog open onOpenChange={(v) => !v && !busy && !uploading && !scanning && onClose()}>
      <DialogContent className="rounded-sm max-w-lg max-h-[calc(100dvh-env(safe-area-inset-top,0px)-2rem)] sm:max-h-[90vh] overflow-y-auto top-[calc(env(safe-area-inset-top,0px)+1rem)] sm:top-[50%] translate-y-0 sm:translate-y-[-50%]"
                     data-testid="load-dialog">
        <DialogHeader>
          <DialogTitle className="font-display uppercase tracking-tight text-2xl">{initial ? "Correct load" : "Add load"}</DialogTitle>
          <DialogDescription>Record the actual movement and its docket.</DialogDescription>
        </DialogHeader>
        {!simple && scanControls}
        {dispatch && <div className="border-l-4 border-[#FF5F15] bg-zinc-50 dark:bg-white/5 p-3 text-xs space-y-1">
          <p className="font-bold uppercase">{simple ? "Current run" : "Assigned run"} · {dispatch.date}</p>
          <p className="font-bold">{dispatch.linked_job?.title || "Trucking run"}</p>
          <p>{dispatch.material}</p><p>{dispatch.pickup_location || "—"} → {dispatch.dropoff_location || "—"}</p>
          <p className="text-zinc-500">Material and locations are filled in. Open Change load details if this load is different.</p>
        </div>}
        {attempt && !busy && <p role="alert" className="text-sm text-amber-700">Save was not confirmed. Retry this same load before logging another.</p>}
        <fieldset disabled={busy || scanning || !!attempt} className="space-y-3">
          {!simple && basicFields}
          {primaryUnit === "load" && <p className="text-sm font-bold">Saving this trip records one load. Weight and volume are optional.</p>}
          <div className="grid grid-cols-2 gap-3">
            {(primaryUnit === "m3" ? [["cubic_m","m³","m3"],["tonnes","Tonnes","tonnes"]] : [["tonnes","Tonnes","tonnes"],["cubic_m","m³","m3"]]).map(([key,label,test]) => <div key={key} className={(primaryUnit === "m3" ? key === "cubic_m" : primaryUnit === "t" && key === "tonnes") ? "border-l-4 border-[#FF5F15] pl-2" : ""}><Label className="label-tag">Actual {label}</Label><Input type="number" inputMode="decimal" step="any" min="0" value={f[key]} onChange={set(key)} className="mt-1.5 h-12 rounded-sm border-zinc-400" data-testid={`load-${test}-input`} /></div>)}
            <div className="col-span-2">
              <Label className="label-tag">Docket #</Label>
              <Input value={f.docket_number} onChange={set("docket_number")}
                     className="mt-1.5 h-12 rounded-sm border-zinc-400" data-testid="load-docket-input" />
            </div>
          </div>
          {!simple && jobField}
          {simple && scanControls}
          <div>
            <Label className="label-tag" htmlFor="load-docket-photo">{f.docket_path ? "Replace docket photo" : "Docket photo (optional)"}</Label>
            <Input id="load-docket-photo" type="file" accept="image/*" capture="environment" disabled={busy || uploading || scanning} onChange={pickPhoto} className="mt-2" />
            {uploading && <p className="text-xs mt-2">Uploading photo…</p>}
            {f.docket_path && <div className="flex flex-wrap items-center gap-3 mt-2"><button type="button" aria-label="View docket photo" onClick={() => setViewPhoto(true)}><AuthImage path={f.docket_path} alt="Docket" className="w-16 h-16 object-cover" /></button><span className="text-xs break-all">✓ {photoName || "Saved docket photo"}</span><Button variant="outline" onClick={() => setViewPhoto(true)}>View</Button><Button variant="ghost" disabled={busy} onClick={() => setF(current => ({ ...current, docket_path: null }))}>Remove photo</Button></div>}
          </div>
          {simple && <details className="border-t border-border pt-2" open={!f.material.trim() ? true : undefined}><summary className="cursor-pointer min-h-12 flex items-center font-medium text-sm">Change load details</summary><div className="space-y-3">{basicFields}{jobField}</div></details>}
          <div>
            <Label className="label-tag">Notes</Label>
            <Input value={f.notes} onChange={set("notes")} placeholder="Optional"
                   className="mt-1.5 rounded-sm border-zinc-400" data-testid="load-notes-input" />
          </div>
        </fieldset>
        {viewPhoto && f.docket_path && <PhotoLightbox paths={[f.docket_path]} startIndex={0} open onClose={() => setViewPhoto(false)} />}
        <DialogFooter className="sticky bottom-0 bg-white dark:bg-[#101620] py-3">
          <Button variant="outline" onClick={onClose} disabled={busy || uploading || scanning} className="rounded-sm uppercase tracking-wider font-bold text-xs">Cancel</Button>
          <Button onClick={save} disabled={busy || uploading || scanning}
                  className="h-12 rounded-sm bg-[#FF5F15] hover:bg-[#E65100] text-white uppercase tracking-wider font-bold text-xs"
                  data-testid="load-save-btn">
            {busy ? "Saving…" : attempt ? "Retry same load" : initial ? "Save correction" : "Save load"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
